import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  AccountCircle,
  CalendarMonth,
  KeyboardArrowDown,
  AdminPanelSettings,
  MenuBook,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import logoHeader from '../assets/logo-header.png';

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Anchor elements for each dropdown
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);
  const [mealPlanAnchor, setMealPlanAnchor] = useState<null | HTMLElement>(null);

  // Handlers for opening menus
  const handleAccountClick = (event: React.MouseEvent<HTMLElement>) => {
    setAccountAnchor(event.currentTarget);
  };

  const handleMealPlanClick = (event: React.MouseEvent<HTMLElement>) => {
    setMealPlanAnchor(event.currentTarget);
  };

  // Close handlers
  const handleCloseAll = () => {
    setAccountAnchor(null);
    setMealPlanAnchor(null);
  };

  // Navigation handlers
  const handleNavigate = (path: string) => {
    handleCloseAll();
    navigate(path);
  };

  const handleLogout = () => {
    handleCloseAll();
    logout();
    navigate('/login');
  };

  return (
    <AppBar position="sticky" color="primary" elevation={2}>
      <Toolbar sx={{ justifyContent: 'space-between', gap: 2 }}>
        {/* Logo — left */}
        <Box
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}
          onClick={() => navigate('/dashboard')}
        >
          <img src={logoHeader} alt="Snack'd" style={{ height: 40, width: 'auto' }} />
        </Box>

        {/* Navigation Menus — right side */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
          {/* Recipes */}
          <Button
            color="inherit"
            startIcon={<MenuBook />}
            onClick={() => navigate('/recipes')}
          >
            Recipes
          </Button>

          {/* My Account */}
          <Button
            color="inherit"
            startIcon={<AccountCircle />}
            endIcon={<KeyboardArrowDown />}
            onClick={handleAccountClick}
          >
            My Account
          </Button>
          <Menu
            anchorEl={accountAnchor}
            open={Boolean(accountAnchor)}
            onClose={handleCloseAll}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => handleNavigate('/account-settings')}>
              Account Settings
            </MenuItem>
            <MenuItem onClick={() => handleNavigate('/family-members')}>
              Family Members
            </MenuItem>
            <MenuItem onClick={() => handleNavigate('/user-preferences')}>
              Family Preferences
            </MenuItem>
            <MenuItem onClick={() => handleNavigate('/membership')}>
              Membership
            </MenuItem>
            <MenuItem onClick={() => handleNavigate('/contact')}>
              Contact Us
            </MenuItem>
            <MenuItem onClick={() => handleNavigate('/creator-dashboard')}>
              Creator Dashboard
            </MenuItem>
            <MenuItem onClick={() => handleNavigate('/chef-dashboard')}>
              Chef Dashboard
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>

          {/* Meal Planning */}
          <Button
            color="inherit"
            startIcon={<CalendarMonth />}
            endIcon={<KeyboardArrowDown />}
            onClick={handleMealPlanClick}
          >
            Meal Planning
          </Button>
          <Menu
            anchorEl={mealPlanAnchor}
            open={Boolean(mealPlanAnchor)}
            onClose={handleCloseAll}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => handleNavigate('/dashboard')}>
              Today's Dashboard
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleNavigate('/dashboard?mode=plan')}>
              View Schedule
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleNavigate('/dashboard?mode=plan')}>
              Generate New Plan
            </MenuItem>
          </Menu>

          {/* Food & Shopping - Hidden until features are complete */}
          {/* Resources - Hidden until features are complete */}

          {/* Admin Link - Only visible for admins */}
          {user?.isAdmin && (
            <Button
              color="inherit"
              startIcon={<AdminPanelSettings />}
              onClick={() => navigate('/admin')}
              sx={{ ml: 2 }}
            >
              Admin
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navigation;
